import {
  Chapter,
  Character,
  Letter,
  Location,
  Poem,
} from "../types/novellaTypes";

// Define characters for Hatchling of the Orchard
export const hatchlingCharacters: Record<string, Character> = {
  caramansia: {
    id: "caramansia",
    name: "Caramansia",
    alternateNames: ["Cara", "Clara", "Hatchling"],
    image: "/assets/characters/Hatchling/caramansia.png",
    title: "Hatchling of the Orchard",
    affiliation: "Hydaria",
    description:
      "A young girl tasked with protecting the princes of Hydaria. Daughter of the Orchid Mantis, she inherited her parents' role in the secretive order that serves the empire. She is also a bard in training.",
  },
  jeyon: {
    id: "jeyon",
    name: "Prince Jeyon",
    alternateNames: ["Jeyon", "Jaline"],
    image: "/assets/characters/Hatchling/jeyon.png",
    title: "Prince of Hydaria",
    affiliation: "Hydaria",
    description:
      "The older of the two princes, quick witted and can often brandish a sharper tongue than most blades.",
  },
  robar: {
    id: "robar",
    name: "Prince Robar",
    alternateNames: ["Robar", "Rinon"],
    image: "/assets/characters/Hatchling/robar.png",
    title: "Prince of Hydaria",
    affiliation: "Hydaria",
    description:
      "The younger prince, innocent and curious despite the tragedy that befell his family.",
  },
  ferryMaster: {
    id: "ferryMaster",
    name: "The Ferry-Master",
    alternateNames: ["Ferry-Master"],
    image: "/assets/characters/Hatchling/ferry_master.png",
    title: "Ferry-Master of the Royal Pond",
    affiliation: "Hydaria",
    description:
      "A well-built, imposing man who operates a ferry across the Royal Pond.",
  },
  spiceMantis: {
    id: "spiceMantis",
    name: "Spice Mantis",
    alternateNames: [],
    image: "/assets/characters/Hatchling/spice_mantis.png",
    title: "Mantis Agent",
    affiliation: "Hydaria",
    description:
      "A woman with raven black hair and hazel eyes, unlike typical Hydarians. However, typical for Mantis agents, to better blend in with their infiltration targets.",
  },
  scryingMantis: {
    id: "scryingMantis",
    name: "Scrying Mantis",
    alternateNames: [],
    image: "/assets/characters/Hatchling/scrying_mantis.png",
    title: "Spymaster",
    affiliation: "Hydaria",
    description:
      "The compiler of intelligence reports and overseer of the mantis network. Responsible for documenting the events surrounding the Hatchling and ensuring the emperor's lineage survives.",
  },
  orchidMantis: {
    id: "orchidMantis",
    name: "Orchid Mantis",
    alternateNames: ["Orchid"],
    image: "/assets/characters/Hatchling/orchid_mantis.png",
    title: "Mantis Agent",
    affiliation: "Hydaria",
    description:
      "Caramansia's mother, a skilled spy who gave her life protecting the royal family. Her death during the palace conspiracy left Cara to complete their family's mission alone.",
  },
  ragos: {
    id: "ragos",
    name: "High Warmaster Ragos",
    alternateNames: ["Ragos"],
    image: "/assets/characters/Hatchling/ragos.png",
    title: "High Warmaster",
    affiliation: "Hydaria",
    description:
      "The High Warmaster of Hydaria, of his exploits there are too many to list.",
  },
  ithana: {
    id: "ithana",
    name: "Ithana",
    alternateNames: [],
    image: "/assets/characters/Hatchling/ithana.png",
    title: "Healer",
    affiliation: "Nahreath",
    description:
      "A skilled healer from an unnamed town near Gravok. As pure-blooded a Hydarian as they come.",
  },
  lynder: {
    id: "lynder",
    name: "Lynder",
    alternateNames: [],
    image: "/assets/characters/Hatchling/lynder.png",
    title: "Healer's Brother",
    affiliation: "Nahreath",
    description:
      "Ithana's brother who assists her in caring for the travelers.",
  },
};

// Define locations for Hatchling of the Orchard
export const hatchlingLocations: Record<string, Location> = {
  orvabad: {
    id: "orvabad",
    name: "Orvabad",
    image: "/assets/locations/Hatchling/orvabad.png",
    description:
      "The capital city of Hydaria in the north, home to the imperial palace. The site of the devastating tremors and conspiracy that forced the princes to flee.",
    realm: "Hydaria",
    mapCoordinates: { x: 2250, y: 3500 },
  },
  kimora: {
    id: "kimora",
    name: "Kimora",
    image: "/assets/locations/Hatchling/kimora.png",
    description:
      "A lakeside town on the Royal Pond that serves as a hub for trade from northwestern villages. The chaos of the tremors left its harbor in disarray as people sought passage home.",
    realm: "Hydaria",
  },
  royalPond: {
    id: "royalPond",
    name: "The Royal Pond",
    image: "/assets/locations/Hatchling/royal_pond.png",
    description:
      "A massive lake so large some call it an inland sea. Considered a blessing by the Hydarians, it houses a statue to the founder Hida, and offers livelihood to many towns along the heartlands of Hydaria.",
    realm: "Hydaria",
  },
  gravok: {
    id: "gravok",
    name: "Gravok",
    image: "/assets/locations/Hatchling/gravok.png",
    description:
      "The second biggest city in Hydaria. The people of Gravok often claim they were the true pure-blooded Hydarians, and are often at odds with their kin in Orvabad.",
    realm: "Hydaria",
  },
  lobeth: {
    id: "lobeth",
    name: "Lobeth",
    image: "/assets/locations/Hatchling/lobeth.png",
    description:
      "A hidden city where the Mantis Order claims their base. Lost history, current happenings, as well as many secrets of Hydarian and Osgaran history are guarded and archived here.",
    realm: "Hydaria",
  },
  kingsRange: {
    id: "kingsRange",
    name: "King's Range",
    image: "/assets/locations/Hatchling/kings_range.png",
    description:
      "A mountain range that forms a natural barrier in the Hydarian landscape.",
    realm: "Hydaria",
  },
  simronVillage: {
    id: "simronVillage",
    name: "Simron Village",
    image: "/assets/locations/Hatchling/simron_village.png",
    description:
      "A mining village near the mountains. Unremarkable in many ways.",
    realm: "Hydaria",
  },
  nahreath: {
    id: "nahreath",
    name: "Nahreath",
    image: "/assets/locations/Hatchling/nahreath.png",
    description:
      "A destination beyond Hydaria's borders, a free city where denizens of all realms live together without worry of persecution.",
    realm: "Beyond Hydaria",
  },
  mirapor: {
    id: "mirapor",
    name: "Mirapor",
    image: "/assets/locations/Hatchling/mirapor.png",
    description: "A newly formed Motran city directly on The Parting Sea.",
    realm: "Beyond Hydaria",
  },
  opulentDesert: {
    id: "opulentDesert",
    name: "The Opulent Desert",
    image: "/assets/locations/Hatchling/opulent_desert.png",
    description:
      "An anomalous desert that deprives any who dare enter it of a return. It forms the southern border of the road to Gravok.",
    realm: "Hydaria",
  },
};

// Letters for Hatchling
export const hatchlingLetters: Record<string, Letter> = {
  introductionLetter: {
    id: "introductionLetter",
    title: "For Our Enduring Order & Empire",
    content: `FOR OUR ENDURING ORDER & EMPIRE

Tales shall be woven of the events that occurred today, and they will be both exaggerated and inaccurate. Two events that serendipitously took place alongside one another will undoubtedly be conflated.

This is to dispel the mist, and remove obscurities about what happened on this day. So that our noble ruler's lineage remains unbroken, so that Hida's Blood remains untainted.

The plot against the emperor's life has been well and truly foiled.

The seismic catastrophe that befell him was not the work of man.

And while his reign might have ended, I have seen the hatchling, and I have seen her charge. Hida's Blood lives on, though I have deemed it prudent to let it bloom elsewhere for a while, until the ripples that these malevolent tremors have left us finally subside.

Long may Hida's Blood live on,
Scrying Mantis`,
    sender: "Scrying Mantis",
    recipient: "The Order",
  },
};

// Chapter to letter mapping (for unlocking letters after completing chapters)
export const hatchlingChapterToLetterMapping: Record<string, string> = {
  introduction: "introductionLetter",
};

// Poems for Hatchling (unlocked after completing chapters)
export const hatchlingPoems: Record<string, Poem> = {
  poem1: {
    id: "poem1",
    title: "Poem I",
    content: `Mighty oak may split and fall,
While saplings bend but hold their ground,
Sometimes smallest voices call
When noble tongues make not a sound.

Giants topple in their pride,
Yet seedlings weather every gale,
In places where the brave won't stride,
Quiet courage will prevail.`,
  },
  poem2: {
    id: "poem2",
    title: "Poem II",
    content: `Silver waters hide beneath,
Sharp stones that cut feet that wade,
Behind the sweetest smile lies teeth,
The gentlest hands may hide a blade.

But bitter bark can heal the sick,
And thorns protect a rose's bloom,
Darkest clouds bring needed rain,
To fields that withered once in gloom.`,
  },
  poem3: {
    id: "poem3",
    title: "Poem III",
    content: `I drew a tower by the sea,
With windows counting thirty-three,
But when I walked that shore again,
I counted only nine or ten.

The baker's shop, the cobbler's door,
The fountain where we used to play,
I mark them down, then mark once more,
Yet something vanishes each day.`,
  },
  poem4: {
    id: "poem4",
    title: "Poem IV",
    content: `When hunger haunts the market square,
Bread weighs more than golden rings,
Not pounds or ounces balance there,
But desperate and needful things.

What coin is earned in trust betrayed?
What silver buys a mother's tears?
How many fortunes have been made
From trading on another's fears?`,
  },
  poem5: {
    id: "poem5",
    title: "Poem V",
    content: `The rabbit runs but finds no hole,
The bird takes wing but finds no tree,
When solid ground begins to roll,
Nothing waits where it should be.

Familiar paths lead somewhere new,
Through doors that weren't there before,
We chase what once we thought we knew
But find ourselves on distant shores.`,
  },
};

// Define chapters for Hatchling of the Orchard
export const hatchlingChapters: Chapter[] = [
  {
    id: "introduction",
    title: "Introduction",
    content: `Hatchling of the Orchard

A Timeless' Aria Novella
A Prelude to The Ember Tide

"They say birds of a feather ever flock together,
Though not whether their feathers change not forever."

25th OF SECOND SUMMER 499 F.O.

FOR OUR ENDURING ORDER & EMPIRE

Tales shall be woven of the events that occurred today, and they will be both exaggerated and inaccurate. Two events that serendipitously took place alongside one another will undoubtedly be conflated.

This is to dispel the mist, and remove obscurities about what happened on this day. So that our noble ruler's lineage remains unbroken, so that Hida's Blood remains untainted.

The plot against the emperor's life has been well and truly foiled.

The seismic catastrophe that befell him was not the work of man.

And while his reign might have ended, I have seen the hatchling, and I have seen her charge. Hida's Blood lives on, though I have deemed it prudent to let it bloom elsewhere for a while, until the ripples that these malevolent tremors have left us finally subside.

Long may Hida's Blood live on,
Scrying Mantis`,
    containsLetter: true,
    letterId: "introductionLetter",
  },
  {
    id: "chapter1",
    title: "The Parting",
    content: `The Parting

25th OF SECOND SUMMER 499 F.O.

Tears streamed down her smiling face as she ran as fast as her unsure feet could take her.

'I'm smiling father… I'm smiling…'

The palace was awash with guards swarming to the throne room, detaining any who would attempt to leave. Attempting to combat the panic as the world shook repeatedly, demanding their fullest attention for moments at least before they were let back into their self inflicted panic.

No one noticed the young girl running along with two other cloaked children, years younger than she was. Her long straight hair bounced along as she ran, dark brown, unlike all of her Hydarian peers. Her skin was a fair shade of olive, but what she had seen on that day had turned her a pale shade of fair.

"What is happening, Cara? Where are you taking us?" The boy's distressed voice interrupted her thoughts.

'I don't know… I don't know anything.'

"It's alright, I just have to get you to safety. It'll be alright, I promise." She lied as they ran, making full use of the current state of the palace. Disarrayed and disorganised, unprepared for events the like of which were taking place within the palace and outside.

"It is as though a new star has been born in the south!"

"These tremors, will they not end?!"

"Hida's rage is upon us!"

"Quick, men, to the throne room! Let no one exit."

Cara kept running, and it was when she was closest to the exit that she made a turn towards the servant's quarters. The two hesitant boys behind her seemed further distressed when she took this turn, but they trusted the older girl enough to stifle their concerns and follow along. One of them obviously leading the other, and while both of them were younger than Cara, there was a difference of a few years in age between the two boys as well.

She dashed into one of the maid's rooms, and no one was there.

'Odd… they should be here in case… why is this open?!'

"Come along now. It's right through here." Cara pointed at a wardrobe once full of cleaning tools, all of them now spilled on the ground, and the false back of the wardrobe shown to be open. Leading down into a dark place that the older boy did not wish to follow into.

"I'm not going down there…" He decided and shook his head, "I demand to know what is going on? What happened to father, to mother?! To your own mother, for that matter! Is this a ploy? Is this your doing?"

The boy was far too young to have a tongue this sharp about him, but such was his upbringing in Orvabad. He stood there, behind Cara, who looked at the black decline before them almost longingly, while the younger boy looked back at the entrance apprehensively.

Cara turned to look over her shoulders, and as soon as the boy saw her face, he almost gasped. Rivulets of tears flowed down her pristine cheeks that had only ever known to smile. Cheeks that even now were holding onto a faint forced smile. She sighed before him, a reverberating, stuttering sigh that confused him as much as this entire situation had.

"I want to know… where is mother?" The younger asked, and Cara shuddered.

Even now, the two boys managed to remain exceptionally level-headed for the situation they were all thrust into.

"Please, trust me," Cara said, unable to muster any more words.

The older boy looked behind him suspiciously, then uttered a curse he was far too young to know and followed along.

"You had better explain."

"Thank you."

The younger began to take off his cloak when Cara stopped him and led them both into the false back of the wardrobe.

Silently, they went down an ominously long flight of stairs into the dark underbelly of the Hydarian Palace. Cara walked in small steps, making sure she had a hand on the smoothed out stone of this tunnel's interior. She was no stranger to these passageways; she had been taught where to find them, and which to use and when. But this particular passage was saved only for the highest of emergencies, and to find it already open scared her.

'I have to do my part… I'm still trying to smile, father, so please let this all pass.'

The stairs eventually gave way to a long and winding passageway. The tremors still came, and with them dust and rocks crumbled from the top of the tunnel. But these hidden walkways had been built to last, and would suffer their passage safely.

They emerged into a poorly lit room.

Many passageways connected here, but all of them were barred.

In the middle of the room was a round desk, where a man sat next to the only candle in the room and kept poring over documents and ledgers.

As soon as Cara walked into the room, panting as she was, his ears pricked up and he raised his sharp pointy nose and peculiar face to look at them. His back was hunched down, his figure was deformed, his teeth yellow in the dim light, and his hair was tacked on in the form of a few strings atop his head.

"Which of you has returned?" Even his voice was choppy and unpleasant to hear, but Cara recognised him.

"It is I…" she said in all simplicity, and the man raised his candle as though it would shed any more light on her face higher up.

"Orchid's hatchling? Oh no. What have you there? Why have you brought him here?"

"A necessity." She then pulled the younger child to her front, who had been hiding behind her.

"Both of them? Dear me. They are your charge then, are they not? You are Orchid's hatchling mantis, are you not?"

"I am, and they are." Cara's feigned smile broke as her face twisted to try to protect it.

"I will not ask you of what transpired. News will follow you soon." The man hopped up with the candelabra in his hand. "Follow me, young ones. You will make for the nest."

He walked in such a horrid limp, but Cara led the boys along after grabbing a hand each for reassurance.

"You will not leave by the northern road, nor will you take the eastern one." He took them to one door, which he began to feel with his free hand. "You will travel west, through the farmlands and to Kimora. From there, you shall procure a ferry across the pond. South you shall go, look for the statue of Hida and follow her right. Then around the King's Range you shall go and take the old un-trodden road to Gravok, which would lead you to the nest. Is that all clear, hatchling?"

"Will they remain in the nest?" Cara asked, looking down at the sleeping child in her arms, sedated by force.

"That is for the Scrying Mantis to decide, not I. But do humour me this, hatchling. Does the Orchid still bloom?"

"I…" She started, then whatever faint smile she tried to keep had crumbled.

'Mother is dead.'

"The Orchid has wilted away. Though to me… it was a sudden turn of the seasons."

"Shame. Such a lamentable shame." He shook his head in genuine displeasure. "But know that it shall bloom in its hatchling. Now go, Hatchling of the Orchard, that you might bloom again."

Cara left, with the older boy angrily following her in complete silence to one side, and the younger one still in shock. They walked for a while before they began climbing steps again, and now, as they neared an exit, the boy burst.

"You very well know that you have explained nothing to me, nothing!"

"Please… forgive me. I thought we would have time, but we must keep moving."

"No. I want to know why you have dragged me and my brother here, and what that deformed man meant by you taking us to a nest? On whose authority?"

'Have you not been watching? Or are you simply oblivious?'

"Prince Jeyon, please forgive me." She said as they reached an exit where the prince refused to move. "For the past few seasons, I have been involved in a scheme to oust your royal father. Though not to make it happen, but my part was to stop it from happening. My father, my mother, the bards you saw were all major proponents in this plot, and I fear I may never see them again because of it… certainly not mother. So please let me play my part? Let me give what little my family has left to offer, so that yours might one day thrive again."

It was a rare occurrence for Prince Jeyon to be stifled and lost for words, but there he was.

The young prince fell silent again.

"I know you will one day make a fine emperor." She found herself saying, as that familiar wetness blurred her eyes, and heat throbbed in the core of her nose. She walked up to the prince and embraced him. "I know you shall. You did not know any of this would happen, and yet you are already showing strength that I couldn't prepare to show for an entire year. I am sorry, Your Highness. I wish only to live to see the day you rule."

"Your highness I am not… I am simply your charge." He said, still irate with the amount of happenings that he was unaware of. "And I trust you to it, Caramansia. But promise me this, that there be no more secrets from me, or between us."

"None, Jeyon, none." She agreed, and after a moment of solace, she turned to open the exit, and as soon as she did, a bright white light flooded into their eyes. They emerged from the tunnel and into an empty warehouse, and upon exiting that they could see themselves in a meadow of lavender outside the palace.

Inside the city outskirts still.

"Cara, what is that?" Jeyon asked as he pointed at the sky, and she too looked in awe at the white sheets of light suspended along the horizon in the far south.

"I do not know…"

"I think I do."

The younger prince gawked at the sight, jaw dropping at the immense magnitude of the untold phenomenon they witnessed.

"This is Orvabad's farewell, is it not?"

'He has the imagination of a child still… one that I ought yet be.'

"It is only polite that we bid our home farewell too."

"Until we all can return. Soon."

"Yes, soon." She lied again.`,
  },
  {
    id: "chapter2",
    title: "The Ferry-Master",
    content: `The Ferry-Master

29th OF SECOND SUMMER 499 F.O.

Cara stood up above the crest of a hill, with the weary prince tailing her, still climbing. The younger of them had tired out, and so she carried him on her back as she watched the sun slowly peek from beyond the horizon behind her.

It was in front of her that the scene was captivating.

The morning sun barely shone onto The Royal Pond of Orvabad, the massive lake that could span multiple cities on its own. Some took to calling it an inland sea, but the Hydarians took it as their blessing.

"Look, Jeyon." She motioned to the lake just as the young prince completed the climb. "The hard part is over. You'll get to ride a boat across this lake."

Try as she might to remain cheerful and uplifting, the prince's sour mood could not be dissuaded. It took all his mental fortitude to accept the loss of his parents. In fact, Cara thought that the idea still hadn't pierced his unbelieving mind.

'And how could he? When I have not accepted it myself. But no matter, I have been prepared for this. Mother once told me that those of our line of work can only live for a singular purpose at a time. And this is mine, and it has come into full effect.'

"Yes, a boat. So that even running away ceases to be an option." The prince replied irately.

"Oh, come now, we are merely regrouping." She lied again to the prince, as she had grown accustomed to doing over the past few days.

"Tell me something," the prince started as Cara led him down the other side of the hill and towards the serene village of Kimora. "I recall you being around me abundantly as I played, alone or with my brother. I still remember how you would shyly act around us and eventually come to befriend us. Was that all for this?"

Cara's brow broke for a fleeting moment before she recovered and turned back to Jeyon.

'Far too clever, Jeyon, please don't do this to me, not now.'

"I've known both of you for far longer than this, my prince." She mentioned politely.

But Jeyon mercifully fell silent.

"Come now, it is a short trot to Kimora. I'm certain things will improve from there!"

It was indeed a manageably short trek to the lakeside town, but on arrival, they were greeted with a state of general panic. Not as exaggerated as in Orvabad, but still people were watching, pointing, concerned with the curtain of light that now lingered in their sky. Before she ventured any further, she made sure the princes' hoods were raised as she led them through the rising chaos and towards the harbour.

And there was the worst of it.

While it had been a calmer town, Kimora had served to collect trade from the majority of villages northwest of the lake. Many that were not large enough to warrant a spot on most maps. And the kind folk that now found themselves stuck in Kimora were all trying to book passage back to their homes.

This did not help Cara's cause whatsoever.

She went from one Ferry-Master to another, but with such a rush to get to safety, they seemed to have been raising their prices to exorbitant levels.

'Those that have the decency to take on the needy must have already set sail. What rotten luck…'

It was then that Cara spotted something that unsettled her.

Hydarian soldiers with armour she did not fully recognise had made their way to the outskirts of the town, barely visible as they descended from the crest of the hill where Cara had once stood.

'Oh no. I have to think of something…'

She continued to lead the two concealed princes through the harbour. Until she spotted a suspiciously emptier ferry with a large crowd clamouring for admission. It was then that an idea struck her, and immediately she fought her way towards the Ferry-Master, princes in hand.

Then, Caramansia began to sing, though she had to scream to get anyone's attention.

"Tremors shake our very ground,
a hatchling seeks seven stars for light.
The storm too shall pass unbound,
leave what it may of lasting scars.
Afraid and anxious, souls uncrowned,
in need of petals to hide behind,
Yet Orchid wilted, bravery found,
no two ways but to stand refined."

The Ferry-Master took note, and for the briefest of seconds, his eyes widened intensely before he regained his composure. Meanwhile, Cara blushed as she had briefly become the centre of attention over a poem she hastily conjured.

'Not my best work is it… but no matter, it only needs to work.'

"Five stanzas short for a lakeside shanty, don't you think?" He asked, and Cara immediately panicked. This was just the response she needed, but in her anticipation, she missed the message.

'Five! What was the fifth word? How could I not catch it…'

"Forgive me, but my hatchling doesn't need five stanzas… she needs a boat."

The man laughed heartily; he had been standing on a platform surveying the crowd, but after her response, he bent down and plucked her up onto the platform. And for her part, Cara quickly held onto both princes so the man could lift all of them.

"Your pouch of gold, little hatchling." The man demanded.

Cara looked up at him, and her expression fell flat, she'd been injected with hope, but it would seem even that hope came at a cost. She took out a heavy pouch, and the man snatched it to weigh it in his hand. Then he shook his head and looked her up and down.

"Not nearly half enough," he said as he pocketed the pouch. "But it is a long voyage, some additional services along the way might compensate."

It was just as well that this man was incredibly well built, tall and lean, but imposing enough that none of the village folk dared stop what they saw. Even if the outcry was palpable in the gathered crowd, as they watched Cara stand there, frozen.

'What does he mean by that?!'

She was just about to panic when Jeyon tugged on her dress.

His look was unmistakable. It held every bit of his unmitigated scepticism, but paired with imperial authority. He was bidding her to prove her loyalty, and she had to accept the Ferry-Master's request.

The four of them now left the platform to board the modest boat, mostly empty save for two oarsmen and one archer. The crowd behind swarmed up the platform as the Ferry-Master nodded and the boat took off. Some jumped into the lake, some called out for curses, but the boat had set off.

"No, you will not be doing that." Jeyon said, much to the confusion of everyone.

The Ferry-Master shot him a narrow gaze, and Cara immediately moved to shelter the young Hydarian prince.

"We ought to thank the kind man!" Cara quickly blurted.

"At ease, hatchling." The man said and tossed the pouch of gold back to her, and then Cara was truly lost. "Protect your charge, at any cost."

"I'm… confused." Cara let out, still blinking between the ferry master and Jeyon.

"A trait of the times, I reckon." He replied as he took them deeper into the lake where an unsettlingly thick fog began descending upon them. "These lads have a faithful guardian in you, but I fear your road is long. Fraught with… new kinds of dangers. You've started seeing it yourself, haven't you? The world is… changing."

"I only need to reach the nest." She told herself more than anything.

The man half chuckled, but made no comment. Instead, a moment later his expression stiffened.

"Remain silent." He said, further adding to Cara's confusion.

They heard it before they saw it, another boat being rowed vigorously had announced its presence. Cara found herself holding her breath, as though the added modicum of silence would drive it off, but instead, as a hungry animal drawn to its prey, the boat soon broke the fog and found them.

"Hail, Ferry-Master."

"Hail, blades of Hida."

"Hah, we claim no such lofty title, though we would know who it is you are ferrying."

"Has my reputation come under fire of late?" The Ferry-Master asked.

"It is not your loyalty we've come to question, only a probable lack of acquaintance with your passengers."

"Curious time to delve into these matters, eh?"

"Ferry-Master. Your lack of cooperation is unsettling."

"Very well, I must admit. These passengers are not sanctioned for passage." He began, and Cara's eyes bulged momentarily, obscured by the mist. "They do not travel to sow seeds, nor bring goods to the villages. They are… my kin. I wished to save them from the chaos near Orvabad, may you forgive me my greed."

"On any other day we might have cared." The guard said, and his two companions kept squinting for a better look at Cara and the two princes.

"I am spared? To what do I owe this benevolence?"

"To insurgent assassins, and gaudy thieves."

"Assassins? Thieves? Might I convince you to escort us to the shore then?"

"You needn't worry, Ferry-Master. Forget I ever said anything, in fact. As you were!"

The guards quietly drifted off and away, and finally Cara took in a breath, and began panting for air. As tense as the situation was, Robar could not help but chuckle at how their would-be guardian nearly suffocated herself on tension alone.

"Assassins? He can't mean-" Cara began, but the man interrupted abruptly.

"Hatchling, listen. If what this man said can be believed, then I can only surmise that these tremors have somehow caused a breach in the dungeons of Orvabad."

Caramansia fell silent.

"I am certain you know more than a humble Ferry-Master, so I will leave you to your deductions. You have my sympathy; yours was never a simple task, and this complicates matters."

'Complicates matters… would be when a road is blocked or a route is lost. This implies that we've failed twice, that I am being hunted, that they died for nothing… No amount of preparation could possible seem me through this as it stands. How come, then, that your only advice, father… was…'

Cara put on a weak and laboured smile as she turned to Jeyon and Robar, then looked back ahead into the mist, and let herself enjoy the silence that she knew would not last.

Time flew by, and in the constant state of mist and fog, Caramansia could not tell how much time had passed, especially since she had allowed herself to doze off. But with eyes open once more she noticed the boat had slowed down significantly.

"You are with us again, hatchling." The Ferry-Master said.

"Where are we?" Cara asked.

"Nearly there," he replied.

"Are we waiting for something?"

"How observant of you? We await a signal."

"A signal that shall not come." Jeyon spat impatiently.

"Patience." The Ferry-Master urged.

"Just what exactly of anything we've seen thus far inspires patience? We need to act! Now. If we are being chased, and cannot even trust our own guards, and you worry that our identities have been compromised, then bring us to shore and leave us to our false promise!"

The Ferry-Master gawked at Jeyon.

He scrambled to recall when he'd mentioned anything of what Jeyon had blurted out, but then he slowly nodded.

"So it is true; my thoughts are not as protected as I thought them to be around you, your highness." He spoke, and Cara's eyes widened at Jeyon. "If this is your wish, then I shall take you ashore, but do mask your ability, your grace."

"Jeyon, I don't understand…" Cara began, but the boat moved again, and the fog lifted slightly as they approached land.

"Neither are you prepared, from the looks of it." Jeyon hissed back, and Cara felt stunned. Jeyon sighed and then continued, not wanting to sound overly harsh. "It is as you suspect, and I cannot explain it either. It is as though your minds echo within my own, your voices, your thoughts. And yes, it is driving me insane, though one favourable thing that has come of this is that I have confirmed that Robar's mind is indeed barren and empty."

Robar rolled his eyes and sulked at Jeyon, but just as he made for a response the boat rolled into solid ground, and the Ferry-Master stood up and began hawkishly surveying the area.

"Quickly now, and quietly." He urged, ushering all three out of the boat. "So long as this fog holds, you shall be better guarded. Come now, if memory serves, you ought to continue along this shoreline until you find a lightly forested hill. There will be no other hill in sight. Go around the hill along the foot of it, and you will join a path. Keep to that path until you see three mills; it is the third you want. Behind it is a safe-house. Head there, and your path to the nest may be easier, but I am afraid I cannot take you any further."

"Thank you dearly," Cara replied, and the Ferry-Master took out three small brick-like loaves.

"For the road, and hide your gold better, hatchling."

With that, Cara nodded and as abruptly as they had come into the man, they left him behind to go back to his boat and attempt to throw off any pursuers.

It did not prove as simple as he made it out to sound, finding a particular hill when the fog had yet to lift proved impossible for three that had spent most of their lives within city borders. They roamed and wandered along the shore aimlessly, eyes peeled for a hill that would undoubtedly be masked by the fog.

"Only a little more to go," Cara said to encourage them.

"And how do you know that?" Jeyon asked.

"He couldn't have dropped us far, I'm certain we're close now!"

Robar winced in silence, for he too knew it was all she could do to remain unrealistically optimistic. But it was in those moments of feeling lost and alone that he began to sniff and shed tears.

"I know… I know," Jeyon said and stopped to embrace his brother while Cara frowned, not knowing what Jeyon was replying to.

'Look at us… to be as young as they are and to have lost…'

Even in the sanctity of her thoughts, Caramansia could not bring herself to think of the parents they had all lost in a single moment. And they had not stopped for a moment to reconcile.

'The safe-house… it must be near.'

Without a word, Cara went back to the two brothers and gently with arms wrapped around both, urged them to move again. And begrudgingly they all continued.

Until even light itself failed them.

"Cara… we have to stop. We've gone too far already, and we can't see anything anymore." Jeyon complained. "I'm starting to wonder if that man was lying about the hill."

"No he wouldn't!" Cara blurted out, and Jeyon half chuckled.

"I am joking. I read his mind, remember?"

Cara bit her lips, and blushed a bit, though mercifully unnoticeable in the darkening light.

"Fine, I just… did not want you two to rest without a roof."

Jeyon clearly had something to say, but Robar caught him.

"We know, Cara, we know." He said, "I wish we could paint your hair red again and chase around with wooden sticks, but…"

"Come to think of it, she did look better in red and blue," Jeyon added, and Cara pouted much to his amusement. For a moment, all tension was broken as they took to find shelter. A few large rocks beneath a set of trees was all they could manage before they huddled together for the night. Able only to fall asleep out of sheer tiredness, and a faint hope that the next day would bring about hope.

And in hope it began indeed.

When next they awoke, one after the other, they saw a bright sun had dispersed the fog, and they saw as well how far they had overshot the hill. Then there was the issue that they now saw two hills, though one was stranger formed and further away.

'He said there would be one…'

Nevertheless, with renewed hope, they set out once more.

The prevailing scent in the air as they walked was strangely reminiscent of the sea, though they were still far from it.

"That's the road there!" Cara said excitedly, and true enough they joined the way. "We have to be close now! Just a little longer and we'll meet up with people that can help!"

The princes did not share that optimism, but they could not help but begin to hop. As one mill appeared, and gave way to another in the empty morning road they felt a building excitement within them. Until finally, they saw the third mill.

Hope never felt nearer, and yet, even then…

Caramansia stopped them all dead in their tracks.

"Come, you two wait here." She led them to one side of the road and behind a thicket where she left them, and continued alone in spite of Jeyon's protests.

The safe-house was where the Ferry-Master said it would be, but if not for a passing premonition, their journey would have ended there. Cara noted a trail of blood leading into the safe-house, and so with a sinking heart and trembling feet she approached stealthily.

One step after the next towards the safe-house, when she heard a voice in her mind ring loud and clear.

'Cara! Come back! We need to run!'

Freaked out by this, she tumbled into the door, throwing it open, only to reveal two dead men within.

Caramansia fell to her hands and knees, trembling, nearly ready to throw up before her mind went ablaze again with Jeyon's incessant warning. There was not an ounce of strength left in her when she climbed back to her feet, when she noticed one of the bodies clutching onto a rolled parchment.

A rustling noise outside, and raised voices prompted her into action. Her gentle features contorted as she pushed herself towards the dead man, and barely managed to pluck the parchment before she turned, and ran.

'Just smile… just smile!'

She told herself, when she wanted to break down.

When hope had escaped her, and her road seemed to stretch on still.`,
  },
  {
    id: "chapter3",
    title: "The Nest",
    content: `The Nest

30th OF SECOND SUMMER 499 F.O.

In one hand she held onto Jeyon, and with the other she clutched onto Robar as Cara fled frantically from the scene she had beheld there in the safe-house.

"There was fighting, Cara! We saw it!" Robar blurted out as they ran. "Armed men were being chased by villagers; what does it mean?"

"That we're not safe," Jeyon replied as the three of them made for cover, which came in the form of a lightly forested area.

"For the moment! Only for the moment!" Cara insisted as they darted into tree cover and immediately slowed to a cautious pace.

"And for all foreseeable moments…" Jeyon added.

"What do you think happened, Jeyon?" Robar asked.

"Well… remember those dreadful lessons father insisted we take?" Jeyon replied; he was remarkably calm at that moment, although it seemed that his panic had manifested in the form of irate sarcasm. "And remember how the Warmaster had been insisting on harsher grain, supply, and manpower requirements from the villages? I'll give you one chance to guess where we are, and what the people have just lost…"

"I don't understand…" Robar replied, brows furrowed, and upset at what Jeyon was implying.

"Come now, Jeyon," Cara interrupted, "let us not jump to conclusions. The world is confusing right now; we don't need to add to it."

Jeyon had more to say, but he bit his tongue instead.

"Here, you've not eaten." Cara said as she handed out the rations they'd been given, and quietly the two boys ate while Cara sneakily broke her rations into two and kept them in her pouch for later. They continued walking slowly into the forested area, and they thought they heard a distant pecking of sorts.

"What do we do now, anyway?" Jeyon asked as he struggled to chew on the brick-like loaf.

"Well…" Cara began, not knowing herself. "If we head south from here, we should find a statue that shows us a path around the mountains. I know we just need to find the old road to Gravok, and we'll be on the way to safety."

"The statue will lead us to the road?"

"Well yes, but maybe we can find it ourselves. I'm not certain."

"I remember my maps vaguely, but if we are to loop around the mountains… we'll need a cart, won't we?"

"That sounds reasonable, yes."

"Lovely, so we have to interact with rebellious village folk after all."

"Not necessarily," Cara started, then produced the parchment she'd taken earlier.

"How is that going to help? What even is that?"

"I picked it up in the safe-house, it seems members of my order were expecting us, but violence broke out. We were lucky not to be there, but in this I saw a name. 'Simron Village' I think this is where we may find loyal people able to help."

"A shame the name alone doesn't give direction."

"Well, the parchment mentions it was a mining village. So perhaps we only need to find our way to the mountains, and comb along until we find a village."

"I've heard about optimism before, Cara, but this is stretching it."

"Better than going the entire way on foot," Cara started, when they heard a rustling that demanded silence. The pecking had stopped entirely as well.

A moment passed, and nothing emerged.

"Jeyon…" she whispered, "that thing you do, reading minds. Do you have to see your… uhm… target?"

"I don't know, it just happens."

"So, you don't hear any thoughts around us other than our own?"

"Not for the moment."

Cara nodded, and then they heard yet another louder rustling. This one, however, grew louder and louder until it descended into creaking and groaning as a tree collapsed and fell up ahead.

"Be very quiet." Cara's whisper was barely audible as she proceeded with a beating heart to peer through the shrubbery in the direction of the rustling.

Jeyon and Robar followed closely, and then all three of them saw a strange sight. A man stood there, axe in one hand, and another hand on the stump of a tree he'd just felled. Then Cara covered her mouth before she could gasp and pointed at the man's axe.

It was unlike anything they'd seen before. The axe itself seemed alive; roots and tendrils grew from its hilt, and it had begun sprouting out leaves as well. The man inspecting the tree stump seemed just as bewildered in his expression as Caramansia was. He ran his hand alongside the stump slowly, then all of them witnessed as it sprouted once more, and as leaves began to slowly emerge from beneath the man's hand as he lifted his hand gently.

They were witnessing a tree regrow in seconds before them.

"Hida's blessings…" the man whispered, and then of all people Robar emerged from behind the bushes.

"How did you do that?!" He asked innocently.

"No! Wait! I'm not like them!" The man replied instantly, then his brief panic subsided for him to notice that the one questioning him was a child. "Damn, you had me for a moment. Who are you? And what are you doing out here? And… how much did you see?"

Caramansia did not care much for the man's tone.

"I saw all of it! That was amazing. How did you just make that tree grow again?"

"Well, it just… happens really." The man replied, all sense of worry seemed to escape him for the moment.

'Jeyon, can you hear me? I don't like this. What did he mean by - not like them?'

Jeyon could indeed hear Cara's thoughts, and he shrugged, but both of them remained hidden for now.

"But you really shouldn't be out here, whose son are you anyway? It's been getting harder and harder to track these days."

'Jeyon? How trusting is your brother?'

Jeyon closed his eyes and Robar scratched at his head for a moment, a long moment for Caramansia as she held her breath.

"Oh, I'm Cara's boy." He blurted out and the man's brows stitched together.

"Cara? And who is your father, boy?"

"Oh, he… died fighting Mithra."

"He must have been a braver man than I!" The man let out a chuckle then waved a hand. "I mean, I'm genuinely sorry, of course. But this is why we village folk make for poor fighters."

"My father was not a poor fighter."

"Wouldn't be dead now if he wasn't, just the truth, boy."

"It's that stupid Mithra that doesn't fight fair!"

"No one does, and we village folk are too honest for that life."

'Jeyon? Ideas?'

Caramansia urged in her thoughts, but Jeyon was already helping Robar not trip over himself while speaking.

"You really should get going though, boy, your mother is probably worried."

"No! I want to see more of what you did to that tree."

Cara almost chuckled as she watched Jeyon put his head in his small hand watching his younger brother's brash behaviour.

"Not a chance, can't risk this happening to you." The man lifted his hand and showed the full extent of the axe's amalgamation. The tendrils were clearer now, as if it had grown roots and branches that clutched onto the man's hand. And in some parts, joined in with the flesh itself.

Robar saw this, and fell back, then got up and bolted away from the man while screaming. Cara quickly tapped Jeyon on the shoulder and moved in the direction of Robar to intercept him, but Jeyon lingered. He watched as the man just stood there laughing, as he pulled his hand free of the axe in a natural motion.

'Odd child, but not as odd as these blessings from Hida. I just need to hide out here until the chaos is over…'

Jeyon peered into the man's mind before he too had to use the cover Robar had given them to escape unnoticed. It did not take him long to reunite with them, but they seemed to have caught up with Robar outside of the forest, upon an ill-trodden dirt road.

"Your mother?!" Caramansia fumed, fists on her hips as she demanded an answer. "Never mind putting us all at risk to see a tree grow, you called me your mother?!"

"Relax, Cara, it was quick thinking if I do say so myself." Jeyon said, then Cara's ire turned to him.

"Your quick thinking, no doubt. I'm barely older than you two! Couldn't you have said sister, or friend, or anything else?"

"Aunt Cara, go it." Jeyon mocked, and Cara began to chase after him.

For a brief moment they were back in the palace, and her hair was read, they were Hydarian heroes, and she was Mithra chasing after them in a flight of childish fancy.

"Cara, wait!" Jeyon said, but she chased him on.

The moment couldn't last, as Caramansia chased Jeyon along the dirt path and into a far better laid out gravel road.

"Cara!" Jeyon called out again, then she stopped when she noticed the road. "I'm sorry, alright? We need to get off this road."

Then she had a thought, one that Jeyon neglected to read.

"Maybe… maybe we don't."

"Sorry, what?"

"Let me explain."

It did not take them long at all to enact Cara's plan. Simple as it was. They needed transportation west, and a road of this quality was bound to head off towards the villages near the coast or along the mountains. Regardless of which, it would bring them closer to their quarry. So they found a part of the road that was slightly more elevated, and they hid where the land was lower. They kept a vigil for friendlier looking travellers, and remained put, thinking that merchants, miners, or fishermen were bound to cross this path.

"Odd, isn't it?" Jeyon asked as they waited.

"What is?"

"We saw fighting earlier, but there's no sign of violence now."

"Well, we've been keeping away from the villages so far."

"Yes, but I somehow imagined rebellions to involve a lot more scorching of the land, and less… well, calmness of the roads."

"We don't know that there's a rebellion, Jeyon. But you do raise a fair point. Waiting for merchants wouldn't work if there's unrest."

It was at that point that they saw a mule dragging a cart approach, and immediately Jeyon got to work while Cara sprung into action to peer over a few rocks at the travellers.

'Cara, they're worried.' Came Jeyon's voice in her head. 'They're fleeing something.'

Cara nodded, and showed herself to the cart and began to wave, while Jeyon and Robar remained hidden yet.

"Hail, traveller!" she called out as the cart approached.

"Hail," came the man's spent reply. "You one of them?"

"Pardon?" she asked as the man did not seem like he would stop.

"One of them! The blighted ones! Have you been hiding under a rock these past two days?"

The cart was full not of commodities or materials, but rather living equipment. Pots and pans, tents and bedrolls. Cara noticed and kept thinking about it in her mind to relay the information to Jeyon.

"As it so happens, when the tremors happened we were out in the pond fishing when our boat capsized and my father drowned." Cara lied. "I… I just need to get to the mountains. If your path takes you there, then I would be grateful. I can pay too!"

"Are you, or are you not, one of them?"

"I don't know who they even are, I'm sorry!"

"Can't risk it… no, can't risk it at all. Mob will drown you if you show any trace of the blight."

"Would you at least tell me what's happening?"

Cara began walking alongside the cart as the man continued.

"No one knows! They say the emperor and his sons were killed, that the tremors were a sign of a third coming of Hida… and everyone's lost their minds. The Duchess of the Pond hasn't been popular around these parts for years now, so the rabble have taken it upon themselves to un-duchess her. If you have any sense, you wouldn't go east, at least I wouldn't!"

"Well then, you can't leave me here! I'm not blighted, I swear! I'm no different than I was two days ago! Here, I have gold, please help me!"

The man finally slowed his cart down and sighed. He looked down at Caramansia, then looked around himself and nodded into the cart.

"Thank you dearly! Let me call to my siblings."

"Wait, what?"

"It's alright you two! The kind man will let us ride along, come on out." She called out to them, then turned to the man. "I'm sorry, they're just petrified, look at them."

Jeyon and Robar cautiously approached, both with their cloaks on and hoods up. The man was by now giving Caramansia a cruel gaze, but she continued.

"Oh, I'm Clara by the way, and this is my youngest brother, Rinon, and my younger sister, Jaline. She has a heart of gold, but she's mute."

Robar stifled a giggle while Jeyon shot Cara a venomous gaze.

'Jaline?! Seriously? I have half a mind to claim that I am the Emperor of Hydaria and demand my formal address right here-'

"And we're all eternally thankful for the ride along. We wouldn't have managed without you." She said sweetly, and went to stand behind Jeyon and Robar.

'Trust me, Jaline. We wouldn't want to match the description of two boys and a girl fleeing Orvabad now, would we?'

Jeyon fumed, but he did so in silence as the man began muttering to himself.

"Ah! And of course, as a token of our gratitude." Cara said, and produced a handful of coins, far more than the journey would ever cost, but not too much to become immediately suspicious. Or so she deemed.

"Well, go on then, get in!" The man said, accepting the gold, and clearly feeling somewhat reassured because of it.

Cara helped the princes up and, for a while, they felt respite.

"Children are far too clever these days, I'll say." The man grumbled, and Cara thought it prudent to take his mind off her antics.

"Say, may I ask what you meant by that… blight?"

"If you've not yet seen it, you've been spared."

"Is it that bad?"

"One man burst into flames and scorched his entire household; another gave rise to a hill that devoured him whole. That's not the worst of it either. I've seen a man try to escape capture only to fling himself by some unseen force violently into a tree. Splattered himself all over it, the poor lad. Then some people claim they can control this blight, but of course the mob's the mob. Won't listen to reason."

"You said they wanted to depose the duchess?"

"Her and her noble blood, it's been brewing for a while mind you. But the tremors, then the emperor's death? They figured now would be as good a time as any. Aye, but the mob's not nearly half as dangerous as those escaped criminals I saw last before I left, hard times all around."

"So this blight, you said the mob won't listen to reason, is it really a blight?" Cara made sure to ask in the most inquisitive voice she could arrange, and the man felt himself tasked with guiding her because of it.

"Look here, Clara." He began, "Nothing in this world is black and white. A swordsman is either a hero or a villain depending on whom he kills, and who's calling him this or that. Whatever these people had, I wouldn't call it a blessing like some of them do, but I don't think it's a blight either."

Cara nodded along, but it was Robar who was listening intently.

'Cara, that man has unusual powers himself; I just caught it.'

She immediately swallowed hard as she heard Jeyon's thoughts ring in her own mind, but she would make no mention of it.

In fact, the rest of their journey was spent between idle chatter and carefully curated answers from Cara that would draw no further attention. Even if it took their mule-pulled cart five days of travel where Jeyon had to remain silent and fume in Cara's thoughts exclusively. The man proved to be hospitable, though wary of others, though he had warmed up to Cara, which led to him telling her far more than she cared to learn about himself. Though again, Robar listened, intrigued and absolutely coloured by the notion of experiencing the lives and troubles of commoners in a way he never would in his high halls.

Sure enough, five days later they reached a fork in the road, and almost ceremoniously the man left them to their own devices and continued northward bound.

"Well! I dare say things are starting to look up," Cara announced as she led the way onto a lightly used smooth road.

Jeyon crossed his arms and did not speak.

"I'm afraid Jaline is angry, Clara," Robar said, poking fun at his cross brother, who just shot him a gaze full of daggers.

"Cheer up, Jeyon." She said, but clearly did not mean it. "Besides, I'm certain we're getting you both to safety, and you'll be rid of me in no time."

"Why would we be rid of you?" Robar asked innocently.

"Well, I…" she frowned, not knowing what to say. "I never really thought about… well, anything after I get you two safe. You two will have better people to take care of you than I; that's the important part! But well…"

"Surely you'll just stay with us, won't you?" Robar asked again.

"I mean if you want me to, but only if I'll be useful, and then again I don't know if it's mine or ours to really decide. I'm sure I'll be trained, or put to use somewhere. The order that's protected your family for generations is strong and has eyes everywhere, and I'm sure I'll serve it as well as mother and father did. You'll see! I've only heard mother mention this in passing, and she made me swear never to bring it up, but we're almost there anyway, so I can't help it. The city we're heading towards is one built by my order, and it is off any maps. No one knows it exists, and no one who ventures past this road ever returns alive. Why, I'm sure there's someone tracking us right now, and they'll come find us in no time!"

No-time turned into a full day and a half of marching.

Low on food, weary once again, but driven by hope they continued, and the road improved over time.

Until at some point it ended abruptly.

"Um, where do we go from here?" Robar asked, as Jeyon remained irate and silent.

But Cara had no answer. She stood there, trying to fathom what they were now looking at. And slowly, her brows curled, arms fell, and her spirit faltered.

The road did not just end; the very land itself that carried the road had been torn asunder. Broken as if by some malevolent force, sheered off and shattered, and there only remained a cliff where the road would go, and the sea.

The vast, endless, empty sea.

Then they heard a voice.

"Lobeth is no more, little mantis."

Words that made Caramansia hold herself in despair.`,
  },
  {
    id: "chapter4",
    title: "The Power",
    content: `The Power

8th OF THIRD SUMMER 499 F.O.

'Not too close now, last time I scorched it and mother scolded me…'

Cara had cut out a small portion of a Hydarian red wax candle and was slowly getting it to melt in her makeshift sealing warmer. Though she was far removed from the proper set her mother had afforded her, and was instead relying on cutlery and another candle to attempt the feat.

The wax had a rich glitter to it as it slowly melted, all contours coalescing into one shapeless mass within Cara's spoon. She watched, waited and then quickly poured it onto a rolled-up parchment and applied the seal.

'Mother would be proud.'

With her parchment rolled, waxed, and sealed, she got to her feet and exited the tent she had been given to work and approached the woman that had called out to them at the remains of Lobeth.

The woman watched and waited, and she was unlike many that the two princes had seen. Her hair was a deep shade of raven black, her eyes were hazel and her skin slightly darker than any they'd see in Hydaria. And yet, she claimed herself to be of the same order as Caramansia.

Jeyon had never given it much thought before, but Caramansia herself was unlike pure-blooded Hydarians. Slowly the gears turned in Jeyon's mind as he attempted to connect the dots.

"My full report, Spice Mantis," Caramansia announced, and the woman nodded. The Spice Mantis had informed Caramansia of the fate that befell their nest, the hidden city of Lobeth. Of how the tremors were particularly brutal in this part of the world, and of how the city perched upon the cliff had entirely collapsed into the sea along with most of the road that led up to it.

A thousand years of Hydarian knowledge was now wilting away deep within the confines of a tumultuous sea.

"May I ask what purpose this serves?" Cara blurted.

"This report, little mantis, is a testament to our endurance."

Cara nodded quietly, but she struggled to put on any expression other than rank indifference.

"I have told you of what happened here, but I have yet to tell you of what will happen next."

At that, Cara looked up attentively.

"You will have heard of the escaped insurgents when the dungeons of Orvabad cracked, and you will notice that luck has been on your side."

'Has it? I couldn't tell.'

Jeyon nearly chuckled at Cara's mental snide, reminding her that he was always listening.

"The most dangerous assets of the insurgency have returned to Gravok through the southern road. Only a few opportunists ventured north to destabilise the backbone of our realm. However, it would appear as though the northern nobility have not been kind to their subjects. There is a revolt of sorts brewing there, and meanwhile I have learned that a power struggle over the capital is in full swing. I tout High Warmaster Ragos to emerge victorious, though whether that is cause for celebration is yet to be seen."

"Ragos has been a friend of our family and our most faithful servant." Jeyon said, arms crossed. "I doubt he would ever bring harm to us."

"Doubt and certainty are two very different things, young prince." Spice continued, "I would not rely on instinct alone here. Suffice to say that your orders, Hatchling, are to take your charge out of Hydaria and remain there until either you are contacted by our order, or you deem it absolutely clear that a return will see our Emperor's lineage restored. Do I make myself abundantly clear?"

Caramansia nodded even as her head fell slowly.

"That's it? We are to escape like hunted dogs when this empire is ours by Hida's blood?" Jeyon fumed.

"You and I know this truth, Prince Jeyon, but the masses know only that they eat less, earn less, and live worse than you and the entire nobility. Change is dangerous, and this is a time of change, young prince." Spice spoke, but Jeyon remained unconvinced.

"Ill are the masses should we be driven out by our own people."

"Don't say that." Robar interrupted his brother. "Our people are good; they always were, and they will continue to be so. They are confused, and so are we."

"Confused enough to damn us to live amongst our enemies, brother? Because that is what we set out to do now."

"I know they're good, Jeyon. I… I don't want to leave either. I'd rather stay here and find good people to support us, but well…"

"But you fear to test that theory? You fear to remain and find only ravens gnawing at this empire's corpse?"

"No! Jeyon, face it, we're children. I don't want to make a decision now that I may grow to regret when I am older. If Spice says we should leave, then we should leave. But only if Cara agrees."

'And so the younger brother may be more fit to rule, somehow.'

Jeyon stole an angry look at Cara before she put her hands over her mouth as though that thought had happened out loud.

'Jeyon! I didn't mean it like that.'

"Fine! Damned would I be if I ever set foot back in this wretched place without being begged back." Jeyon fumed and threw his hands in the air. "Do what you will; all reason died with father and mother, it seems!"

Spice sighed but looked back at Caramansia. There was more.

"Hatchling, I know I have told you to leave, but I have not yet told you how. I am afraid I cannot help you much in this regard, but all I can offer is advice. Avoid the desert; none that enter it ever return. Avoid the north; it grows far more tumultuous with each passing day. Avoid the mountains; you are ill-equipped to traverse them."

"But, Spice, that leaves…"

"Precisely so; the road through Gravok is your best chance. And Nahreath may be your best destination. Your burden grows heavier by the day, Hatchling. Though the heavier the burden, the better I find is the reward."

Spice had given them at least enough rations to last for weeks, as well as a sturdy pony to carry them along. It was all she had to give, but out of fear of drawing attention to them, she would remain behind. She also needed to guide any of their order that returned to find landscape where they expected a city.

The journey would still be arduous. They followed the road that ran along the base of the mountains from the far west of their faltering empire. To their left, they saw them as massive palisades guarding Orvabad and the heartlands of their empire from the world. The King's Range, that most extreme of mountainous regions. Then, to their right, they saw another extremity of Osgara, an anomalous desert that deprived any who dared enter it of a return. Wedged between the mountains and The Opulent Desert their road took them, and save for a few unsuspecting villagers or townsfolk it had been entirely empty.

And even when they encountered villagers, there seemed to be stability and peace here in the south of the Hydarian empire. The Sire of Gravok held an iron grip on his province, and it showed.

Three weeks of travel, however, saw their rations depleted, and their morale at an all-time low. This could not have come at a worse time as they neared Gravok itself.

"I don't care if we get caught, Cara, we've not eaten a thing in days. We need to buy something to eat," Jeyon complained.

"Prince Jeyon, Gravok is far too dangerous to restock."

"You don't count Robar fainting yesterday too dangerous? If not for his health, then for how slow we were during. Cara, we need to eat."

"I… I'm sorry." Robar managed to utter but Jeyon shook his head.

"No, brother, you have nothing to apologise for. If we had any semblance of planning, this wouldn't have happened."

Cara's lips twitched, but she had learned to blank her mind of thoughts. She had been saving her own rations for them, she'd lost days of sleep to remain vigilant, and her mind had been eaten away with stress. And here they were demanding more of her and criticising the one person that was giving everything to keep them safe. At least Jeyon was.

"Very well. But we shall not enter Gravok. A tavern by the roadside, and I go in alone."

There were no further complaints that day as they drove their weary pony along the outskirts of Gravok, and finally past the eastern gate of Gravok they found a tavern.

'The Whistling Bird.' Or so it was called.

Cara hopped off and helped Jeyon and Robar down, and helped them pull up their hoods and told them to remain there until she returned. Though Jeyon clearly had heard these words too many times, and he waited for her to enter the tavern before he and Robar followed. They remained outside, but just close enough to peer through windows and for Jeyon to intrude.

Meanwhile, Caramansia entered the tavern and slowly walked up to the tavern keeper. Oblivious of the two princes nearby.

"How can I help you, lass?" The man asked, an older man he was. With all the hallmarks of a man who had spent a lifetime in the fields and was now winding down.

"Good day to you," Cara started. "I find myself in short supply and on the road for a while yet. I was wondering if you could help me restock."

"You're in luck; we have just the thing for the young traveller." The man said, "I can offer you dried fruits and nuts, oats and hard bread, as well as aged cheese if you'd like. Depending on how much you'd like to spend, I can fetch some salted meat as well. How far is your trip?"

"A few weeks," Cara replied without thinking. "I may not have enough for salted meat, but the rest sounds lovely."

"Travelling alone, or should I account for companions?"

"Just for two." She said again, finding it only slightly strange that her young age was not questioned.

"This won't be cheap, lass, do you have the coin for it?"

Caramansia simply pulled out a small handful of coins and showed them to the tavern keeper discreetly, and he nodded in response.

"I'll be a moment then." The man said and left through the door behind the counter. Leaving Caramansia to watch the rest of the tavern, which was only slightly busy. Her ears did pick up odd conversation from around her.

"It's like I said, they won't be pleased we're back."

"Even after all we've gone through?"

"The bald madman certainly made it clear he intends to follow. He might be here any day now, and we accomplished nothing."

'No way. I'm hearing things. I'm thinking of things. I'm finding similarities where none exist. These can't be insurgents. I can't be this unlucky. Stay calm, just stay calm.'

"Well, the bald madman can go harvest himself then; I'm not breaking my back anymore."

'False alarm, just calm down, calm down.'

"Little lady? Mind coming back here? I collected your rations. Come see if you need more or not." The tavern keeper called out, and Caramansia's heart nearly escaped her. She nodded and slowly made her way around the counter and through the door, where she indeed saw rations laid out before her.

'Calm down, breathe, be normal, be composed. I'm just here to buy food, nothing bad will-"

Her thought was interrupted by a painful ringing in her head as a pan was brought down on her head hard, and then her mouth was gagged so as to not scream.

'How?! Why?!'

"Scream, and you die." The man said as he dragged her deeper into the room and down into the cellar. The man quickly bound her hands and legs and shoved her against a wall and clutched onto a butcher's cleaver from a nearby table.

"Listen here and listen well, you're going to tell me everything I ask about, is that clear?"

Her eyes went wide, her head had been trickling blood from the impact with the pan, and she had never been more afraid in her life. They both heard a thud near the second entrance of the cellar from the outside, but the man was focused completely on her.

"What do you know of the insurgency?"

"Nothing!" Cara blurted and then felt a heavy fist crash into her midriff. She'd have thrown up if she'd eaten anything in days; she'd have cried if she hadn't been as dehydrated as she was.

"I- I'm sorry!" She cried again, her voice cracking as the man grabbed her by her neck and fixed her against the wall harshly.

"That's not what your mind spoke. Now tell me, before this cleaver sees some action. What do you know about the insurgency? Answer now!"

'Oh no, not him too! I have to- I can't- I must.'

"What you must do is speak. Now!"

"I know nothing about it! Just that I want nothing to do with it!"

"Why are you here?"

Cara wept, having nowhere to run, not even in her mind, but tears still could not flow down her cheeks.

"Answer!" the man demanded as he pulled her head back and then pushed her again into the cobbled wall behind her, making her wince and scream again. His touch was rough, his treatment was brutal, and yet he knew how to tread the line to not let her faint, not yet.

For her part, Caramansia was doing all she could to blank her mind, Jeyon's constant peering had given her some training on the matter but this situation was entirely different.

"I'm running away! I'm running away from everything!" She cried when the man struck her again.

"You're lying. Dark-haired girl travelling here, paying with Orvabad coin for weeks of travel? Suspicion doesn't begin to describe it, and whatever you're doing with your mind, stop it. Speak. Or think. Or else."

'I'm no one! I'm nothing! I don't care anymore, I don't! Kill me, kill me so I'm with my family again. Kill me so I'd have done my part, as miserable as it was. I'm sorry, I'm so, so, sorry! I can't, Father, I just can't anymore. There is nothing left for me to smile about. Let it all end. Let me fail in silence. Let me know that I at least tried…'

It was in that moment of resignation, when the man's grip around his cleaver had tightened, that a loud, mind-shattering roar was heard from right within that cellar.

The very foundations of the structure were challenged as wave upon wave of lavender-like sparks shot out and the man collapsed to his knees, holding his head with both hands.

"Cara! Run!" came Robar's voice, and there she saw them. They had followed her and found an entrance to the cellar, and there she saw him.

Jeyon's eyes glowed a deep violet colour, his arms were slightly spread and his hands were malformed into a strange clutch as he kept flicking his wrist about and causing wave after violent wave to shatter walls and pillars. Then the man himself was viciously flung into the same wall he had been using to torment Cara, but with such force that he blacked out on the spot.

"You are not nothing! You are to us beyond loyalty! Damned would I be should I let anyone harm you!"

Jeyon's voice echoed, both in their minds and aloud, as the young prince could not control his own powers. The people above began to panic and flee the tavern, not understanding the source of the tremors they were now experiencing.

"You have failed nothing, you have failed no one, not yet. And damned would Jeyon be if he should let you fall, Cara!"

Even in her bloodied state, even in her emotional turmoil, and even in his dangerous loss of control state, Cara flung herself at Jeyon into a warm, loving embrace. And the prince's arms limped as one last violent shockwave erupted from him, causing the walls, the roof, the flooring above to all shatter in a spectacular eruption of wood, stone, and straw. But finally, Jeyon had calmed down, though as he regained control his head dropped and it seemed as though he was drained of life.

"You two saved me," Cara said in a broken voice, but she looked at Robar and brought him into that tight embrace. "I swear, I swear I will serve you both to the day I die." She wept, then turned her gaze to Robar. "Help your brother back to the pony, I'll get us our food."

Robar nodded as he watched Cara hurry back up the ruined stairs, climbing them as though they were a mountain, and they all counted their lucky stars that the debris had spared them as it landed back down from Jeyon's final eruption.

But then there was another issue.

Just as Cara had wrapped some of the rations that had been prepared, or at least whatever she could find in the wreckage, she saw and heard cries that northern banner-men were approaching.

"What is it now?" She rushed back to help Robar and Jeyon get to their startled animal, and the three of them saw a flood of men, armed to the teeth, approaching Gravok.

"Ragos…" Cara whispered as she quickly helped Jeyon and Robar onto the pony and began leading it off the road. The sun had been setting, and they had been lucky to only narrowly leave the main road when they saw streams of red banners trickle along past them.

"Cara, I know these colours!" Robar whispered excitedly.

"I do too, Robar, but I don't know if we can trust them."

"Are we really going to avoid our own Chalice-Keepers and run away?" Robar understood why they would do so, but at the moment he couldn't help but feel the weight of an opportunity slip by. Jeyon was in no state to talk, and Caramansia had her clear orders.

"Robar, I know, I know that this is difficult. But I swear, I swear on this life of mine that you both just saved, that I will do all that I can to see you return. But for now, I know that we control just one thing. And if I give you both to Ragos, then I control nothing. Let's get you safe, let's hide for a while, and let time show if Ragos truly is as loyal as you both remember him to be."

Robar nodded, and looked at Jeyon, still with his eyes closed.

"Do you think he'll be alright?"

Caramansia then stroked the fainted prince's hair and lightly caressed his cheek as she looked upon him.

"He'd better. I didn't get all this food for him to doze off."

At that, Robar chuckled, and once again under the cover of night they set off into the unknown.

But as they set off, Cara could not help but beam.

In her darkest hour, she'd been given new purpose, and as she looked at these two alongside her, she grew certain once more that hope was just over the horizon.

'It has to be, it just has to be.'`,
  },
  {
    id: "chapter5",
    title: "The Parting Sea",
    content: `The Parting Sea

29th OF THIRD SUMMER 499 F.O.

Jeyon awoke in a daze. He was uncertain how long he'd lost consciousness, but he opened his eyes to the thankful beaming face of his brother.

"Cara! He's awake!"

Caramansia had been walking next to the pony, albeit with a limp from her bruises, to give Jeyon more space to rest. As soon as she heard Robar, she stopped and turned to face Jeyon.

"Where are we…" he muttered.

"On the road!" Cara replied enthusiastically.

Jeyon looked at her and then frowned.

"Why isn't there anything in your empty head, Cara?"

She half-rolled her eyes, and put her hands on her hips.

'Very funny, Jaline.'

But Jeyon didn't react at all; she just frowned and held his head in frustration. He gritted his teeth and muttered in quiet pain, which immediately triggered alarm bells for Cara.

"Jeyon?"

"It's fine… just a headache… I'm…" his voice trailed off as Robar began to shoulder his brother to keep him upright.

"Here, have some water, and some nuts as well." Cara offered, but as he grabbed the waterskin and began to drink, his head rolled and he collapsed onto Robar once more.

"Cara? What do we do now?"

"I don't know… let's keep going until we reach a village; we'll ask for help there. I just don't know what's happening with him…"

It was all they could do to keep going, but it would not be another day until they found a village, a small mining village it seemed with a market square and not much else.

"Excuse me," Cara started as she reached a fruit stall. "My brother is ill. Would there happen to be a healer in this village?"

"Well, good day to you too, dear." It was an older woman who tended the stall, hair as golden as it could've got. A pure-blooded Hydarian by Gravok's standards, and a reminder that they were still close to the insurgent city. "Are you certain it's not for you, dear? You look bruised and worse for wear."

"No, no, I'm fine, I just fell." She lied blatantly. "But my brother keeps fainting, and I'm worried."

"That does sound worse than bruises," the woman paused and then sighed. "Sadly, the army marched through here recently. They took most people capable of fighting or healing. I'm sorry, but you're out of luck here."

"Oh… I see." Cara frowned, but went ahead and bought some fruits from the woman before she took the pony with Robar and Jeyon atop it and continued.

'What am I to do? If Ragos' banner-men took healers from these villages, then… do I need to head to Migora? Is that it? Or far enough t Yokuk or Grahir? Is it even safe there?'

"Excuse me, miss?"

Cara stopped and looked and saw a boy her age approach from the side. Out of instinct, she took a step back, her most recent trauma still fresh on her mind.

"Easy there, I only mean to help. I heard you were in need of a healer?" The boy asked, and Cara nodded tentatively. "Well, my sister is… learning. And she loves to help people, so maybe she could help? She lives just outside the village, and she wouldn't charge or anything, promise!"

'And what am I to make of this?'

Cara looked at the boy and then back at Jeyon. The young prince would've called this oddly suspicious, but he would have also been able to read the boy's mind and know his intention. Robar, meanwhile, was nodding, urging her on.

'I just know I will regret this, but Jeyon…'

One prince had urged her into danger because of his selfishness; now, the other was urging her into it out of a kind, loving heart.

"Lead the way." She blurted out and continued along.

'We're doomed to suffer for each other, perhaps.'

The solace of knowing that Jeyon was not awake to read her mind gave her some reprieve to think as she pleased, but then again, she knew to keep her guard up at all times. The weapon they used could so easily be used against them.

They made their way along a twisting, winding road up along the side of the mountain and midst a few trees, and with every step they took Cara felt the urge to turn and run.

"Ithana will be delighted, I promise." The boy said as they climbed. "I'm Lynder, by the way, happy to meet all of you."

'Lynder and Ithana, dear me, those are some archaic Hydarian names.'

"I'm Clara, this is Rinon, and my sick brother here is Jalin."

"Mind me asking what do you think is wrong with him?"

"I was hoping Ithana would be able to tell us."

"Fair," the boy chuckled as they reached the simple-looking hovel nestled at the end of that incline. It was such a cosy, serene spot to live out one's life. Far away from the world's troubles, and yet close enough to see people at one's fancy. It was surrounded by lush greenery and protected by many an incline, and in the garden they saw a young woman tending to flowers. Both brother and sister were fair-haired and of quintessential pure-blooded Hydarian complexion, which worried Cara briefly.

'It is only slightly ironic that three dark-haired Hydarians come to them for aid, though I know it is only their lords that drive a wedge between us.'

"Lynder, are you pestering people again?" Ithana said as she got up and bowed politely in front of Cara and the two boys. "Do forgive us; he gets excited about helping people, I do hope he's not bothered."

"Oh, not at all. In fact, we would be grateful if you could help us." Cara found herself saying, "My brother keeps fainting. I think he overexerted himself recently, and I'm not sure what to do."

"Oh no, well let's take a look at least."

Cara held her breath as Ithana came over and, with Robar and Lynder's help, they took Jeyon down and laid him on his back near a bed of flowers. Ithana sat behind Jeyon's head, and put her hands on his forehead, and she frowned. Then Ithana took a deep breath and looked up at Cara.

"I may be able to help him, though his need is dire."

Cara's hands immediately shot to her chest out of worry.

"But I must confess," Ithana tensed, "I will use a blessing I have been given to mend him. I am not trained in this. I do not know whether what I am doing is right or whether it will work, but I follow only what my blessing shows me. Shall you allow me to attempt?"

'Oh Jeyon… why am I so helpless always?'

Cara nodded and sat next to Jeyon and held his hand as Ithana closed her eyes and moved her hands to Jeyon's temple. She rubbed with her thumbs as she felt her hands heat up, then she grimaced.

"He is… more than spent…" Ithana rasped as her hands glowed a faint tinge of green and Jeyon began to cough violently. "Settle down, young one, calm down. There is much you need."

They watched as time dragged on, and Jeyon began to sweat. He clutched onto Cara's hand as Ithana continued to weave her blessing into his mind, and then her eyes widened.

"Is something the matter?" Cara urged, but the woman had already scrambled to her feet and began rubbing her hands together.

"He spoke to me, in my mind." She spat, "You did not tell me his ailment was of this nature."

"How was I to know?" Cara defended herself as Jeyon suddenly coughed hard again and pulled himself upright, panting.

"Jeyon!" Cara blurted, then realised her slip of the tongue.

Ithana narrowed her eyes at the name but made no response.

"My head… thank you… whoever you are, thank you…" Jeyon finally managed to speak, but Ithana remained silent.

"You are weary yet. Sit." She told Jeyon, but he shook his head.

"No, there is little time. We need to move." Jeyon decided, but Cara frowned, uncertain why he was so eager to push himself. "I did not mean for this to happen, but I fear we might endanger you if we remain here."

"I know." Ithana replied, "Your Highness."

Cara frowned and took to her feet quickly.

"Rest easy, he spoke of it in my mind. I mean you no ill will, but I did not realise I would be aiding escaped royalty today."

"I am so terribly sorry I had to lie," Cara began as she helped Jeyon to his feet. "We have gold; could we pay you? For your aid, for your silence?"

"If you were a penniless peasant, I would have healed you; that you are a claimant to the throne matters not to me. I do this out of a desire to help, though I hope you recall this day, Prince Jeyon. Should you ever return to your throne. A day when the mercy of your kin might have saved your life."

At that, Jeyon kneeled, and bowed his head low, before Cara pulled him back up, and Robar watched all this intently.

"I do not begrudge any of you your lies, and I do not think the words of a reclusive healer would do much to sway the course of empires, though I at least have hope."

Jeyon nodded as they exchanged farewells, and quickly departed. That day Jeyon would need to eat and drink more than usual, but he would also claim that his head had never been clearer. As though a blockage had been removed, as though his strange powers were escaping him and claiming his life along with them, but that woman's healing was such that it bound both together tightly and reinvigorated his life force, giving him even greater control over his power. But he would rest it for the time being.

Their journey carried on, and again they avoided major cities or towns. They kept a cautious pace along calmer roads, often leaving the road altogether as people approached. They appeared in villages only when their supplies dwindled, and only Cara would visit on her own. On one occasion three weeks into their travel, after they had passed the city of Yokuk, they found a man mending a broken down cart, and while Cara's instinct was to move off the road or ignore him, Robar insisted they would help.

"Hail there," Cara began, and the man turned.

"Oh yes, hail, traveller."

"Might we lend you a hand?" She asked.

"You might, and I'd be grateful for it, but I'd be incapable of recompense."

"None needed; call it road manners. Especially on roads as dangerous as these."

"Hah, I wouldn't call these roads dangerous, not anymore at least."

Cara hopped own along with Jeyon and Robar, and at the man's instruction they began to help lift the cart so he could properly affix the wheel to it.

"Not dangerous? Weren't there armies nearby?"

"Oh, not anymore. Apparently, word from the south is that Mithra and Lysiac clashed in the forest, and as it turns out they don't call it 'The Foreboding Forest' out of a desire to scare children. Something happened there, and both armies were wiped clean. Yes, it is as you heard. Mithra is dead, and so is Lysiac, as well as the two armies that followed them."

Cara did not know how to react, but it was certainly not by keeping her end of the cart held up.

"The cart!" Jeyon yelled, and she quickly held on again.

"What do you mean both armies were… destroyed?"

"Just what I mean, they even say a dragon was seen flying above the forest, but I think it's an exaggeration."

'Mithra and Lysaic, both just dead?!'

"What else have I missed?"

"Hang on," the man said as he finally set the wheel and let them drop the cart and started to hammer at a part he thought was not aligned. "Well, I wager you've noticed people with unnatural abilities in the world. It seems some people over at Motra have begun formalising these abilities. They call it magic, and have made four or five categories of it. Most folk are using colours to categorise, though. Yellow for land-based magic, blue for water, red for fire, green for plants, things of that nature."

'Lavender for mind magic! Jeyon! That's the same colour your eyes shone in the cellar!'

"I'm sorry, this is just a lot to take in! What else is new?"

"Other than that, they say the emperor died in a coup, but predictably Ragos holds control over the throne, though of late it felt as though he was the emperor, anyway. But in Migora, without Mithra, and having lost their biggest army to date, there are rumours of brewing unrest. They don't know what they lost when that bloody woman died. There's nothing to bind the Ralms together anymore, well, unless Alon still has any measure of charisma or his nobles refuse to undermine him."

"That's… a lot…"

"It's a new world alright, oh thank you by the way."

"Please think nothing of it. Oh, do tell me. We're on the right path to Nahreath, right?"

"Well, you are, but we've not had merchants come from the south for a while now. Venture at your own risk."

"Thank you for the warning."

"No, thank you for the aid!"

And with that, they parted ways, and the growing sensations of dread and anticipation bubbled within all three of them. They were alone, and they were nearly out of Hydaria, and that would have been the furthest any of them had gone from Orvabad.

The world had changed, and it was now open before them.

Cara dared let herself hope again, and when they had cleared Grahir, and when they were out of Hydarian influence, she dared encourage the two princes that had only grown more and more homesick by the yard.

"We're nearly there, you two! I can feel it."

"Cara, you've been saying that for days."

"Yes, well, I feel it more than normal today."

"I envy that ability to laugh in the face of reality, I do." Jeyon said.

"It's something my father taught me. To smile in the face of anything. Nothing would be too big, nothing too difficult when I just smile and laugh."

Jeyon fell eerily silent, as though he had no real memories of his emperor father to share. As though the more he dug for memories, the more he saw their playmate Caramansia be more present than his own parents.

"For example, I can picture it now! Just over this hill, I'm sure we'll see it! The road to Nahreath, paved and beautiful, and all for us."

Jeyon laughed, but out of sympathy for her simple dreams.

"And a tamed stallion and royal carriage will be there ready for us, no doubt." Jeyon joked, and Cara made a face in response.

They bickered for a bit, but Robar remained silent, his gaze pressed behind his back, watching his home pull away, a home he was forced to leave behind.

"Here it comes, just over this hill, you'll see!" Cara said again, challenging Jeyon, and it was then that Robar looked ahead, only for the pony to halt as all three of them saw an eerily familiar scene, but one they did not expect to meet here.

"Cara? Where's the road? Where's the land? Where's anything?"

Once again, before them stretched a sea. Wild and angry.

The pony continued as Cara held her breath. She had been through much, but this was too much.

"I…" she closed her eyes and thought, then grit her teeth as visions of a moment worse than this came to her. She took a deep breath, held back tears, then she head Jeyon.

"Well, you've brought us this far. You'll bring us all the way to Nahreath, Cara. I'd trust no one else to do it."

Then there were tears. Unlike when she wanted to weep and couldn't, now she sniffed and sobbed but of joy. Never did she think that the two princes she had been assigned as a playmate to would one day bond with her or rely on her as they did now.

'Thank you, Jeyon. An empire worthy of your rule does not yet exist, but until it comes to be, I hope I can continue to serve.'

'You mean more to me than a mere subject, Cara.'

"We're in your hands, Cara." Robar now said too.

'And whatever you do from here, you can do no wrong.'

More tears welled in Cara's eyes, but as she bit her lips she realised that sentiment alone was not going to help them cross the sea ahead.

Then a thought came to her.

"How do we even know that there is land on the other side?"

"That man from earlier," Jeyon noted, "He spoke of Motra, so it must exist somewhere."

"True! Well then, if Motra still exists, perhaps we can head in that direction. If I remember my maps, we head east along this new sea, and eventually we'd reach Motra…"

"Cara? Do you see what we see?"

They all fell silent as they watched.

A massive torrent of water suddenly rushed in as though the sea was still filling up. It crashed against the land before them and tore chunks of rock and land, swallowing it into the sea as it continued.

Waves like mountains rose and collapsed again, water in amounts they could not believe continued to pour into the sea, and then they saw the water begin to coalesce around a point. A cocoon of water lifted higher into the air before it erupted and a bright white light blinded them.

Then the ground beneath their feet began to crack.

"Cara?!"

She looked around frantically, and quickly climbed up the pony and in her only instinct that moment she embraced both of them, shielding them with her body. She did not know why she acted in that way, but soon they were drenched in water, and all manner of stones showered down upon them.

But that was the least of their concerns.

The ground beneath them cracked further and began to drift apart.

"Hold on tight! Just hold on!"

"Cara we need to turn back!"

"It's too late; just trust me! Hold on for your lives!"

'Just… trust me…'

There was so little for her to do at that point, they had come so far, only for her to see the land crack beneath them, and fall off slowly into the tumultuous sea.

'We'll make it, we'll make no matter what.'

She bit her lips to the point of bleeding; she held onto the princes with such conviction, and yet they all fell into the sea all the same. Where even her tears would've been drowned out.

Into that ravenous sea they fell, and there the doom of Hydaria would be written. But it was a kinder doom than they could've expected.

The same white light that had blinded them earlier had lifted them from the sea, catching them and their pony, as well as the ground beneath their feet. They hovered in the air for a moment, suspended there in disbelief as the light appeared to look at them back, her long white hair and white robes blinding still, an arm made of glass shimmered as she touched the bubble of water that surrounded them.

"I did not mean to bring you harm, travellers."

"I know not who you are, but thank you! We are bound south, but we found no road there; we meant not to intrude!" Cara replied.

"A road, yes. You will find a bridge to cross this sea to the east. I will sustain it so long as I draw breath. But as I have frightened you, allow me to assist you."

In a moment, in the blink of an eye, they found themselves on the other side of the sea. With the figure having left them and gone back to her work.

"What was that?!" Robar blurted out, and Jeyon could not bring himself to speak.

"A miracle," Cara let out, but as she looked from one startled prince to the other, she burst out laughing.

She was holding onto them still, but she kissed them one of after the other, and could not let go.

'I'm doing it, father… I'm doing it!'

She wept as she held on to them for dear life.

And she smiled.

Oh, did she smile.`,
  },
  {
    id: "epilogue",
    title: "Epilogue",
    content: `Epilogue

15th OF SECOND SPRING 508 F.O.

She sat in a busy tavern on a stool on a small stage where all could see, and in her hand was a well-crafted lute that she was focused on tuning intently.

A gentle, serene smile never left her lips as she sat there waiting, when out of the corner of her eyes she caught the shadow of Jeyon running past the tavern window. And naturally, he was being chased by three of his age, undoubtedly having offended them in some profound way.

She giggled to herself as she collected her hair and got ready to play. She could not have thought this would be where her life would lead her, but here she was, earning her keep in the taverns of Mirapor.

"Ladies and gentlemen of Mirapor," a man yelled for attention. "Back once again by popular demand for your entertainment, the wonderful, the joyous, the ever melodic, Caramansia!"

In fact, having inherited a lot of her parents' abilities and talent for performance and songwriting, she had earned a reputation for herself. Even being called to sing and perform on occasions in Migora and south in Motra itself.

But on that day, she was in Mirapor, a city they had helped build from the ground up. And one they grew to love, so much so that Jeyon had even adopted it as his namesake, as could be heard in the street outside.

"You'll all rue the day any of you chased Jeyon of Mirapor!"

Caramansia, however, had a performance. Though none in her audience could truly understand the meaning behind her song.

***

"They say birds of a feather ever flock together,
Though not whether their feathers change not forever.

The sparrow born in golden nest,
May learn to sing a beggar's song,
And find among the humble pressed,
A place where it can just belong.

The dove that wore a noble ring,
May shed its crown and learn to roam,
Yet still through all its wandering,
In its heart it carries home.

The sparrow settles, glad to stay,
Content though colours mark its birth,
While dove still dreams of yesterday,
And longs to find its place of worth.

Feathers fade and colours turn,
Yet hearts remember what they've known.
One finds peace in what they've learned,
One aches for seeds that once were sown."`,
  },
  {
    id: "poems",
    title: "Poems",
    content: "",
    containsAllPoems: true,
  },
];
